import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Instagram, Building2, Home as HomeIcon, ArrowUpRight, CalendarRange } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";
import { BUSINESS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_OWNER = {
  name: "", email: "", phone: "", message: "",
  preferred_contact_method: "", property_address: "", city: "",
  bedrooms: "", bathrooms: "", property_type: "", is_furnished: "", property_status: "",
};

const inputCls =
  "rounded-lg bg-white border-[#E7E5E4] focus-visible:ring-1 focus-visible:ring-[#8A9A86] focus-visible:border-[#8A9A86] h-12";
const selectCls = `${inputCls} data-[placeholder]:text-[#78716C]`;

const InfoTile = ({ icon: Icon, label, value, href, testid }) => {
  const inner = (
    <>
      <Icon className="w-5 h-5 text-[#8A9A86]" strokeWidth={1.5} />
      <div>
        <p className="text-xs tracking-[0.15em] uppercase text-[#78716C]">{label}</p>
        <p className="text-[#1C1917]">{value}</p>
      </div>
    </>
  );
  const cls = "flex items-center gap-4 p-5 rounded-2xl border border-[#E7E5E4] bg-white transition-colors duration-300";
  if (href) {
    return (
      <a data-testid={testid} href={href} className={`${cls} hover:border-[#8A9A86]`}>{inner}</a>
    );
  }
  return (
    <div data-testid={testid} className={cls}>{inner}</div>
  );
};

export default function Contact() {
  const [params] = useSearchParams();
  const type = params.get("type");
  const isOwner = type === "owner";

  const [form, setForm] = useState(EMPTY_OWNER);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setForm(EMPTY_OWNER); }, [type]);
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleOwnerSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city || !form.message) {
      toast.error("Please fill in your name, email, phone, city, and property details.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, inquiry_type: "owner" });
      toast.success("Thank you — your consultation request has been sent. Our team will contact you shortly.");
      setForm(EMPTY_OWNER);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------- OWNER VIEW (unchanged behavior) --------------- */
  if (isOwner) {
    return (
      <section data-testid="contact-page" className="px-6 md:px-12 pt-36 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-16 lg:gap-24">
          <div>
            <Reveal>
              <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Property owners</span>
              <h1 className="mt-5 text-5xl sm:text-6xl tracking-tighter font-light leading-none text-[#1C1917]">Partner with Masaya</h1>
              <p className="mt-8 text-lg text-[#44403C] leading-relaxed font-light max-w-md">
                Tell us about your property and we&apos;ll schedule a consultation to discuss a professional management partnership.
              </p>
            </Reveal>
            <Reveal delay={0.1} className="mt-12 space-y-4">
              <InfoTile testid="contact-email" icon={Mail} label="Email" value={BUSINESS.email} href={`mailto:${BUSINESS.email}`} />
              <InfoTile icon={HomeIcon} label="Management partnerships" value="Family-owned, full-service management" />
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={handleOwnerSubmit} data-testid="owner-form" className="rounded-2xl border border-[#E7E5E4] bg-[#F5F5F4] p-8 md:p-10 space-y-6">
              <div>
                <h2 className="text-2xl font-normal tracking-tight text-[#1C1917]">Let&apos;s talk about your property</h2>
                <p className="mt-3 text-[#44403C] font-light leading-relaxed">
                  We&apos;d love to learn more about your property and discuss whether it may be a good fit
                  for Masaya Hospitality. Complete the form below, and our team will contact you shortly.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="o-name" className="text-[#1C1917]">Full Name</Label>
                  <Input id="o-name" data-testid="owner-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your full name" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="o-email" className="text-[#1C1917]">Email</Label>
                  <Input id="o-email" type="email" data-testid="owner-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="o-phone" className="text-[#1C1917]">Phone Number</Label>
                  <Input id="o-phone" data-testid="owner-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(512) 555-0123" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1C1917]">Preferred Contact Method</Label>
                  <Select value={form.preferred_contact_method} onValueChange={(v) => update("preferred_contact_method", v)}>
                    <SelectTrigger data-testid="owner-contact-method" className={selectCls}>
                      <SelectValue placeholder="Select one" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Email">Email</SelectItem>
                      <SelectItem value="Phone Call">Phone Call</SelectItem>
                      <SelectItem value="Text Message">Text Message</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="o-address" className="text-[#1C1917]">Property Address <span className="text-[#A8A29E]">(optional)</span></Label>
                  <Input id="o-address" data-testid="owner-address" value={form.property_address} onChange={(e) => update("property_address", e.target.value)} placeholder="Street address" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="o-city" className="text-[#1C1917]">City</Label>
                  <Input id="o-city" data-testid="owner-city" value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="e.g. Austin" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="o-beds" className="text-[#1C1917]">Number of Bedrooms</Label>
                  <Input id="o-beds" type="number" min="0" data-testid="owner-bedrooms" value={form.bedrooms} onChange={(e) => update("bedrooms", e.target.value)} placeholder="e.g. 4" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="o-baths" className="text-[#1C1917]">Number of Bathrooms</Label>
                  <Input id="o-baths" type="number" min="0" step="0.5" data-testid="owner-bathrooms" value={form.bathrooms} onChange={(e) => update("bathrooms", e.target.value)} placeholder="e.g. 3" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-[#1C1917]">Property Type</Label>
                  <Select value={form.property_type} onValueChange={(v) => update("property_type", v)}>
                    <SelectTrigger data-testid="owner-property-type" className={selectCls}>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single-Family Home">Single-Family Home</SelectItem>
                      <SelectItem value="Townhome">Townhome</SelectItem>
                      <SelectItem value="Condo">Condo</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1C1917]">Is the property furnished?</Label>
                  <Select value={form.is_furnished} onValueChange={(v) => update("is_furnished", v)}>
                    <SelectTrigger data-testid="owner-furnished" className={selectCls}>
                      <SelectValue placeholder="Select one" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="Partially">Partially</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1C1917]">Current Property Status</Label>
                <Select value={form.property_status} onValueChange={(v) => update("property_status", v)}>
                  <SelectTrigger data-testid="owner-status" className={selectCls}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Vacant">Vacant</SelectItem>
                    <SelectItem value="Owner Occupied">Owner Occupied</SelectItem>
                    <SelectItem value="Tenant Occupied">Tenant Occupied</SelectItem>
                    <SelectItem value="Available Soon">Available Soon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="o-message" className="text-[#1C1917]">Tell Us About Your Property</Label>
                <Textarea id="o-message" data-testid="owner-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Location, condition, timeline, and anything else you'd like us to know..." rows={5} className="rounded-lg bg-white border-[#E7E5E4] focus-visible:ring-1 focus-visible:ring-[#8A9A86] focus-visible:border-[#8A9A86] resize-none" />
              </div>
              <button
                type="submit"
                data-testid="owner-submit-button"
                disabled={submitting}
                className="w-full rounded-full bg-[#1C1917] text-white px-8 py-4 hover:bg-[#44403C] transition-colors duration-300 font-medium text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Request a Consultation"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    );
  }

  /* --------------- DEFAULT / GENERAL CONTACT VIEW (no form) --------------- */
  return (
    <section data-testid="contact-page" className="px-6 md:px-12 pt-36 md:pt-40 pb-24 md:pb-32">
      <div className="max-w-4xl mx-auto">
        <Reveal>
          <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Contact</span>
          <h1 className="mt-5 text-5xl sm:text-6xl tracking-tighter font-light leading-none text-[#1C1917]">Get in touch</h1>
          <p className="mt-8 text-lg text-[#44403C] leading-relaxed font-light max-w-2xl">
            For general questions, business inquiries, corporate partnerships, or property questions,
            reach us directly. If you&apos;re planning a stay of 30 nights or longer, please use our
            dedicated Long-Term Stays inquiry system.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InfoTile testid="contact-email" icon={Mail} label="Email" value={BUSINESS.email} href={`mailto:${BUSINESS.email}`} />
        </Reveal>

        {/* Corporate & Extended Stays with Long-Term CTA */}
        <Reveal delay={0.15}>
          <div data-testid="corporate-cta" className="mt-6 rounded-2xl border border-[#E7E5E4] bg-[#F5F5F4] p-8 md:p-10">
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-[#E8ECE7] flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-[#748570]" strokeWidth={1.5} />
              </span>
              <div className="flex-1">
                <p className="text-xs tracking-[0.15em] uppercase text-[#78716C]">Corporate &amp; Extended Stays</p>
                <p className="mt-2 text-lg text-[#1C1917]">Flexible terms available on request.</p>
                <p className="mt-4 text-[#44403C] font-light leading-relaxed">
                  Planning a stay of 30 nights or longer? View our furnished stay options, monthly
                  rates, and submit your preferred dates.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/long-term-stays"
                    data-testid="contact-longterm-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-white px-7 py-3 text-sm font-medium hover:bg-[#44403C] transition-colors duration-300"
                  >
                    <CalendarRange className="w-4 h-4" strokeWidth={1.75} />
                    Request Long-Term Availability
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                  </Link>
                  <Link
                    to="/owners"
                    data-testid="contact-owners-cta"
                    className="inline-flex items-center gap-2 rounded-full border border-[#E7E5E4] bg-white text-[#1C1917] px-7 py-3 text-sm font-medium hover:bg-[#F5F5F4] transition-colors duration-300"
                  >
                    Property Owner Inquiry
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="mt-10 flex items-center gap-4">
          <a data-testid="contact-instagram" href={BUSINESS.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-11 h-11 rounded-full border border-[#E7E5E4] flex items-center justify-center text-[#44403C] hover:border-[#8A9A86] hover:text-[#8A9A86] transition-colors duration-300">
            <Instagram className="w-4 h-4" strokeWidth={1.5} />
          </a>
          <span className="text-sm text-[#78716C]">Follow us on Instagram for updates and new units.</span>
        </Reveal>
      </div>
    </section>
  );
}
